import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../../../lib/api';
import type { DateRange } from './useAnalyticsData';

const STALE_TIME_10MIN = 10 * 60 * 1000;
const GC_TIME = 30 * 60 * 1000;

export interface SourcePerformanceItem {
  source: string;
  leadCount: number;
  wonCount: number;
  conversionRate: number;
}

export interface DeviceBreakdownItem {
  deviceType: string;
  sessions: number;
  leads: number;
  percentage: number;
}

export interface GeoPerformanceItem {
  country: string;
  leads: number;
  percentage: number;
}

function toNum(v: unknown): number {
  if (typeof v === 'number') return v;
  return Number.parseFloat(String(v)) || 0;
}

export function useSourcePerformance(organizationId: string | undefined, dateRange: DateRange) {
  return useQuery<SourcePerformanceItem[]>({
    queryKey: ['analytics', 'campaign', 'source-performance', organizationId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getSourcePerformance({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return (data as SourcePerformanceItem[]).map((item) => ({
        ...item,
        leadCount: toNum(item.leadCount),
        wonCount: toNum(item.wonCount),
        conversionRate: toNum(item.conversionRate),
      }));
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_10MIN,
    gcTime: GC_TIME,
  });
}

export function useDeviceBreakdown(organizationId: string | undefined, dateRange: DateRange) {
  return useQuery<DeviceBreakdownItem[]>({
    queryKey: ['analytics', 'audience', 'devices', organizationId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getDeviceBreakdown({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return data;
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_10MIN,
    gcTime: GC_TIME,
  });
}

export function useGeoPerformance(organizationId: string | undefined, dateRange: DateRange) {
  return useQuery<GeoPerformanceItem[]>({
    queryKey: ['analytics', 'audience', 'geo', organizationId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getGeoPerformance({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return data;
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_10MIN,
    gcTime: GC_TIME,
  });
}
